import type { ColumnConfiguration, FieldConfiguration, ListBuilderConfiguration } from '../types';

/**
 * PD-17146 / PD-17147 — the Vendors and Price Changes tabs of a customer's
 * Items Insights. Rows come from GET /clients/:id/vendor-sales and
 * GET /clients/:id/price-changes, paged on the server. Unit prices leave the
 * decimal count to the tenant's price-precision setting (Candor prices run to
 * five places), so a change on a sub-dollar item is not rounded away. ClientItemHistory maps
 * each Price Changes list id to the window and direction it asks the API for.
 */

const lineBreak = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
});

const field = (
	id: string,
	fieldName: string,
	displayStyle: FieldConfiguration['displayStyle'],
	options: { secondary?: boolean; decimals?: number; date?: boolean } = {},
): FieldConfiguration => ({
	id,
	dataConfig: { type: 'field', field: { fieldName } },
	styleConfig: {
		size: options.secondary === true ? 'xs' : 'sm',
		colorRule: {
			type: 'theme',
			themeColor: options.secondary === true ? 'textSecondary' : 'textPrimary',
		},
		formatOptions:
			options.date === true
				? { dateFormat: 'date-short' }
				: options.decimals != null
					? { decimalPlaces: options.decimals, currencyDisplay: 'symbol' }
					: undefined,
	},
	displayStyle,
});

const column = (
	id: string,
	headers: Array<string>,
	fields: Array<FieldConfiguration>,
	preset: 'xsmall' | 'small' | 'medium' | 'large' = 'small',
	flexible = false,
): ColumnConfiguration => ({
	id,
	fields,
	headers: headers.map((text, index) => ({ id: `${id}-h${String(index)}`, text })),
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: flexible ? 'flexible' : 'fixed', preset },
	verticalAlignment: 'middle',
});

/** One Vendors-tab list: grouped by vendor or by manufacturer (the API's groupBy, picked by list id). */
const salesGroupList = (
	id: string,
	name: string,
	groupLabel: string,
	sortOrder: number,
): ListBuilderConfiguration['lists'][number] => ({
	id,
	name,
	type: 'default',
	active: true,
	cacheTTL: 600000,
	columns: [
		column(
			`${id}-group`,
			[groupLabel, '', 'Products'],
			[
				field(`${id}-name-field`, 'name', 'text'),
				lineBreak(`${id}-lb-1`),
				field(`${id}-products-field`, 'productCountLabel', 'text', { secondary: true }),
			],
			'large',
		),
		column(
			`${id}-sales`,
			['Sales', '', 'Share'],
			[
				field(`${id}-spent-field`, 'spent', 'currency', { decimals: 2 }),
				lineBreak(`${id}-lb-2`),
				field(`${id}-share-field`, 'sharePercent', 'percent', { secondary: true, decimals: 1 }),
			],
		),
		column(`${id}-units`, ['Units'], [field(`${id}-units-field`, 'quantity', 'number', { decimals: 0 })], 'xsmall'),
		column(
			`${id}-last-purchase`,
			['Last Purchase'],
			[field(`${id}-last-purchase-field`, 'lastPurchasedDate', 'date', { date: true })],
			'xsmall',
		),
		column(
			`${id}-top-products`,
			['Top Products'],
			[field(`${id}-top-products-field`, 'topProductsText', 'text', { secondary: true })],
			'large',
			true,
		),
	],
	searchEnabled: true,
	searchPlaceholder: `Search ${groupLabel.toLowerCase()}s...`,
	sortOrder,
});

export const CLIENT_VENDOR_SALES_CONFIG: ListBuilderConfiguration = {
	lists: [
		salesGroupList('client-sales-by-vendor', 'By Vendor', 'Vendor', 0),
		salesGroupList('client-sales-by-manufacturer', 'By Manufacturer', 'Manufacturer', 1),
	],
	paging: { defaultPageSize: 25, pageSizeOptions: [25, 50, 100] },
	filters: { activeFilters: [], defaultFilters: {} },
};

export const CLIENT_PRICE_CHANGES_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'client-price-increases-90-days',
			name: 'Increases, Last 90 Days',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			columns: [
				column(
					'price-change-product',
					['Product', '', 'Item #'],
					[
						field('price-change-name-field', 'itemName', 'text'),
						lineBreak('price-change-lb-1'),
						field('price-change-number-field', 'itemNumber', 'text', { secondary: true }),
					],
					'large',
					true,
				),
				column(
					'price-change-prices',
					['New Price', '', 'Was'],
					[
						field('price-change-price-field', 'price', 'currency'),
						lineBreak('price-change-lb-2'),
						field('price-change-previous-field', 'previousPrice', 'currency', { secondary: true }),
					],
				),
				column(
					'price-change-percent',
					['Change'],
					[field('price-change-percent-field', 'changePercent', 'percent', { decimals: 1 })],
					'xsmall',
				),
				column(
					'price-change-date',
					['Changed'],
					[field('price-change-date-field', 'priceChangedDate', 'date', { date: true })],
					'xsmall',
				),
				column(
					'price-change-last-paid',
					['Last Paid', '', 'Last Bought'],
					[
						field('price-change-last-paid-field', 'lastPaidPrice', 'currency'),
						lineBreak('price-change-lb-3'),
						field('price-change-last-bought-field', 'lastPurchasedDate', 'date', { secondary: true, date: true }),
					],
				),
			],
			searchEnabled: false,
			sortOrder: 0,
		},
		{
			id: 'client-price-changes-12-months',
			name: 'All Changes, Last 12 Months',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			columns: [
				column(
					'price-change-product',
					['Product', '', 'Item #'],
					[
						field('price-change-name-field', 'itemName', 'text'),
						lineBreak('price-change-lb-1'),
						field('price-change-number-field', 'itemNumber', 'text', { secondary: true }),
					],
					'large',
					true,
				),
				column(
					'price-change-prices',
					['New Price', '', 'Was'],
					[
						field('price-change-price-field', 'price', 'currency'),
						lineBreak('price-change-lb-2'),
						field('price-change-previous-field', 'previousPrice', 'currency', { secondary: true }),
					],
				),
				column(
					'price-change-percent',
					['Change'],
					[field('price-change-percent-field', 'changePercent', 'percent', { decimals: 1 })],
					'xsmall',
				),
				column(
					'price-change-date',
					['Changed'],
					[field('price-change-date-field', 'priceChangedDate', 'date', { date: true })],
					'xsmall',
				),
				column(
					'price-change-last-paid',
					['Last Paid', '', 'Last Bought'],
					[
						field('price-change-last-paid-field', 'lastPaidPrice', 'currency'),
						lineBreak('price-change-lb-3'),
						field('price-change-last-bought-field', 'lastPurchasedDate', 'date', { secondary: true, date: true }),
					],
				),
			],
			searchEnabled: false,
			sortOrder: 1,
		},
	],
	paging: { defaultPageSize: 25, pageSizeOptions: [25, 50, 100] },
	filters: { activeFilters: [], defaultFilters: {} },
};
